import { Transport } from '../core/transport';
import { Logger } from '../utils/logger';
import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';

/**
 * Minimum number of events before a recording is worth sending.
 * Must include a FullSnapshot + some incremental data.
 */
const MIN_EVENTS_TO_SEND = 3;

/**
 * Minimum recording duration (ms) before we send the first batch.
 * Prevents ghost recordings from background tab session refreshes.
 */
const MIN_RECORDING_DURATION_MS = 2000;

/**
 * Maximum time (ms) to wait for a full snapshot before flushing anyway.
 */
const FULL_SNAPSHOT_TIMEOUT_MS = 10000;

/**
 * Delay (ms) after stop() before allowing a new start().
 * Gives rrweb time to fully tear down internal observers.
 */
const RESTART_COOLDOWN_MS = 100;

export class SessionRecording {
    private transport: Transport;
    private distinctId: string;
    private sessionId: string;
    private windowId: string;
    private logger: Logger;

    private buffer: eventWithTime[] = [];
    private stopFn: (() => void) | null = null;
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private isStarted: boolean = false;
    private isStopping: boolean = false;
    private isFlushing: boolean = false;
    private startedAt: number = 0;

    // Bound handlers for proper cleanup
    private boundBeforeUnload: (() => void) | null = null;
    private boundPageHide: (() => void) | null = null;
    private boundVisibilityChange: (() => void) | null = null;
    private boundFreeze: (() => void) | null = null;

    // Deferred start listener — stored so we can remove it on stop()
    private deferredVisibilityListener: (() => void) | null = null;

    private lastUrl: string = '';
    private firstBatchTimer: ReturnType<typeof setTimeout> | null = null;

    // Corrective snapshot — detects when async-loaded page finishes rendering
    private correctiveObserver: MutationObserver | null = null;
    private correctiveTimer: ReturnType<typeof setTimeout> | null = null;
    private correctiveMutationCount: number = 0;
    private static readonly CORRECTIVE_SETTLE_MS = 1000;
    private static readonly CORRECTIVE_MAX_WAIT_MS = 5000;
    private static readonly CORRECTIVE_MIN_MUTATIONS = 10;

    private readonly BUFFER_SIZE = 100;          // Flush threshold (was 50 — too small, caused constant flushing)
    private readonly FLUSH_INTERVAL_MS = 3000;
    private readonly MAX_BUFFER_SIZE = 2000;      // Hard cap (was 1000 — overflowed during active mouse movement)

    /**
     * Maximum recording duration (ms) before auto-stopping.
     * Prevents runaway data accumulation for long-lived tabs
     * (e.g., dashboards left open for hours).
     */
    private static readonly MAX_RECORDING_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

    // Full snapshot tracking — only set to true AFTER we successfully send
    private hasSentFullSnapshot: boolean = false;
    private bufferCreatedAt: number = Date.now();

    // Instance ID to detect stale callbacks firing on the wrong instance
    private readonly instanceId: string;
    private static activeInstanceId: string | null = null;

    constructor(
        transport: Transport,
        distinctId: string,
        sessionId: string,
        windowId: string,
        logger: Logger
    ) {
        this.transport = transport;
        this.distinctId = distinctId;
        this.sessionId = sessionId;
        this.windowId = windowId;
        this.logger = logger;
        this.instanceId = `rec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }

    start(): void {
        if (this.isStarted) return;
        if (typeof window === 'undefined') return;

        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            this.logger.debug('Deferring recording start — tab is hidden');

            this.deferredVisibilityListener = () => {
                if (document.visibilityState === 'visible' && !this.isStarted && !this.isStopping) {
                    document.removeEventListener('visibilitychange', this.deferredVisibilityListener!);
                    this.deferredVisibilityListener = null;
                    this.logger.debug('Tab became visible, starting deferred recording');
                    this.initRecording();
                }
            };
            document.addEventListener('visibilitychange', this.deferredVisibilityListener);
            return;
        }

        this.initRecording();
    }

    /**
     * Internal: actually initialize rrweb and attach listeners.
     */
    private initRecording(): void {
        if (this.isStarted) return;

        this.logger.debug('Initializing session recording', { instanceId: this.instanceId });
        this.startedAt = Date.now();
        this.bufferCreatedAt = Date.now();
        this.hasSentFullSnapshot = false;
        this.buffer = [];

        // Register as the active instance — stale callbacks from old instances
        // will check this and bail out.
        SessionRecording.activeInstanceId = this.instanceId;

        try {
            this.isStarted = true;
            this.isStopping = false;

            const stop = record({
                emit: (event) => this.handleEvent(event),
                sampling: {
                    // Mouse position every 50ms (20fps) — balances fidelity vs payload size.
                    // 25ms was generating too many events and filling the buffer before
                    // mutation events could be flushed.
                    mousemove: 50,
                    mouseInteraction: true,
                    scroll: 150,
                    // Capture every input change, not just 'last' — 'last' drops
                    // intermediate states which makes form interactions look broken.
                    input: 'last',
                },
                blockClass: 'honch-block',
                ignoreClass: 'honch-ignore',
                maskTextClass: 'honch-mask',
                maskAllInputs: true,
                maskInputOptions: {
                    password: true,
                    email: true,
                },
                recordCanvas: false,

                // inlineImages: false by default — inlining base64-encodes every
                // image into the snapshot, creating massive payloads that blow
                // through sendBeacon limits and cause main-thread jank on
                // image-heavy pages. Customers can opt in if needed.
                inlineImages: false,

                // Collect inline stylesheets — without this, CSS-in-JS frameworks
                // (styled-components, emotion, etc.) that inject <style> tags
                // won't have their styles captured. The replay shows unstyled DOM
                // which looks like "the view didn't change" because everything is
                // in the wrong position/invisible.
                collectFonts: true,

                // Hook into rrweb's mutation processing. Plugins can extend what
                // gets captured — but more importantly, without explicit config
                // rrweb may skip shadow DOM mutations entirely.
                // If the list view uses web components / shadow DOM, this is required:
                // (uncomment if applicable)
                // recordCrossOriginIframes: true,
            });

            if (!stop) {
                this.isStarted = false;
                this.logger.warn('rrweb record() returned null — recording not started');
                return;
            }

            this.stopFn = stop;
            this.flushTimer = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);
            this.lastUrl = window.location.href;

            this.boundBeforeUnload = () => this.handleUnload();
            this.boundPageHide = () => this.handleUnload();
            this.boundVisibilityChange = () => this.handleVisibilityChange();
            this.boundFreeze = () => this.handleUnload();

            window.addEventListener('beforeunload', this.boundBeforeUnload);
            window.addEventListener('pagehide', this.boundPageHide);
            document.addEventListener('visibilitychange', this.boundVisibilityChange);
            document.addEventListener('freeze', this.boundFreeze);

            this.scheduleCorrectiveSnapshot();

            this.logger.info('Session recording started', {
                sessionId: this.sessionId,
                instanceId: this.instanceId,
            });
        } catch (error) {
            this.isStarted = false;
            this.logger.error('Failed to start session recording', { error });
        }
    }

    /**
     * Called by Honch core when SPA navigation is detected.
     */
    onNavigate(url?: string): void {
        if (!this.isStarted || !this.stopFn) return;

        const currentUrl = url || window.location.href;
        if (currentUrl === this.lastUrl) return;

        this.lastUrl = currentUrl;
        this.logger.debug('SPA navigation detected in recording', { url: currentUrl });

        if (typeof requestAnimationFrame !== 'undefined') {
            const capturedInstanceId = this.instanceId;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // Guard: only snapshot if we're still the active instance
                    if (this.isStarted && SessionRecording.activeInstanceId === capturedInstanceId) {
                        this.takeFullSnapshotSafe();
                    }
                });
            });
        } else {
            const capturedInstanceId = this.instanceId;
            setTimeout(() => {
                if (this.isStarted && SessionRecording.activeInstanceId === capturedInstanceId) {
                    this.takeFullSnapshotSafe();
                }
            }, 50);
        }
    }

    private handleVisibilityChange(): void {
        if (!this.isStarted) return;

        if (document.visibilityState === 'hidden') {
            this.flushSync('hidden');
        } else if (document.visibilityState === 'visible' && this.stopFn) {
            // Tab resumed — DOM may have been mutated while hidden.
            // Take a full snapshot to re-sync state, but ONLY if this recording
            // has been running for a bit (not if we just started from a session rotation).
            const timeSinceStart = Date.now() - this.startedAt;
            if (timeSinceStart > 1000) {
                this.logger.debug('Tab resumed, requesting full snapshot');
                const capturedInstanceId = this.instanceId;
                setTimeout(() => {
                    if (this.isStarted && SessionRecording.activeInstanceId === capturedInstanceId) {
                        this.takeFullSnapshotSafe();
                    }
                }, 100);
            } else {
                this.logger.debug('Tab resumed but recording just started, skipping extra snapshot');
            }
        }
    }

    /**
     * Safely attempt a full snapshot.
     */
    private takeFullSnapshotSafe(): void {
        if (!this.isStarted || !this.stopFn) return;

        // Only take snapshot if we're the active recording instance
        if (SessionRecording.activeInstanceId !== this.instanceId) {
            this.logger.debug('Skipping snapshot — stale instance', {
                myId: this.instanceId,
                activeId: SessionRecording.activeInstanceId,
            });
            return;
        }

        try {
            record.takeFullSnapshot();
            this.logger.debug('Full snapshot taken');
        } catch (error) {
            this.logger.warn('Failed to take full snapshot', { error });
        }
    }

    private scheduleCorrectiveSnapshot(): void {
        if (typeof MutationObserver === 'undefined' || !document.body) return;

        this.correctiveMutationCount = 0;
        const startTime = Date.now();
        const capturedInstanceId = this.instanceId;

        this.correctiveObserver = new MutationObserver(() => {
            // Bail if this instance is no longer active
            if (SessionRecording.activeInstanceId !== capturedInstanceId) {
                this.cleanupCorrectiveObserver();
                return;
            }

            this.correctiveMutationCount++;

            if (this.correctiveTimer) {
                clearTimeout(this.correctiveTimer);
            }

            if (Date.now() - startTime > SessionRecording.CORRECTIVE_MAX_WAIT_MS) {
                this.fireCorrectiveSnapshot();
                return;
            }

            this.correctiveTimer = setTimeout(() => {
                this.fireCorrectiveSnapshot();
            }, SessionRecording.CORRECTIVE_SETTLE_MS);
        });

        this.correctiveObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });

        setTimeout(() => {
            // Only clean up if this is still our observer
            if (SessionRecording.activeInstanceId === capturedInstanceId) {
                this.cleanupCorrectiveObserver();
            }
        }, SessionRecording.CORRECTIVE_MAX_WAIT_MS);
    }

    private fireCorrectiveSnapshot(): void {
        const count = this.correctiveMutationCount;
        this.cleanupCorrectiveObserver();

        if (count >= SessionRecording.CORRECTIVE_MIN_MUTATIONS) {
            this.logger.debug('Taking corrective snapshot after DOM settled', { mutations: count });
            this.takeFullSnapshotSafe();
        } else {
            this.logger.debug('Skipping corrective snapshot — DOM was stable', { mutations: count });
        }
    }

    private cleanupCorrectiveObserver(): void {
        if (this.correctiveObserver) {
            this.correctiveObserver.disconnect();
            this.correctiveObserver = null;
        }
        if (this.correctiveTimer) {
            clearTimeout(this.correctiveTimer);
            this.correctiveTimer = null;
        }
    }

    private handleUnload(): void {
        this.flushSync('unload');
    }

    /**
     * Stop recording. Returns a promise that resolves after rrweb has had
     * time to fully tear down (RESTART_COOLDOWN_MS).
     */
    stop(): Promise<void> {
        if (!this.isStarted && !this.deferredVisibilityListener) {
            return Promise.resolve();
        }

        this.isStopping = true;
        this.logger.debug('Stopping session recording', { instanceId: this.instanceId });

        // Deregister as active instance
        if (SessionRecording.activeInstanceId === this.instanceId) {
            SessionRecording.activeInstanceId = null;
        }

        // Clean up deferred start listener if recording never actually started
        if (this.deferredVisibilityListener) {
            document.removeEventListener('visibilitychange', this.deferredVisibilityListener);
            this.deferredVisibilityListener = null;
        }

        // Stop rrweb FIRST
        if (this.stopFn) {
            this.stopFn();
            this.stopFn = null;
        }

        this.cleanupCorrectiveObserver();

        if (this.firstBatchTimer) {
            clearTimeout(this.firstBatchTimer);
            this.firstBatchTimer = null;
        }

        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }

        if (this.boundBeforeUnload) {
            window.removeEventListener('beforeunload', this.boundBeforeUnload);
            this.boundBeforeUnload = null;
        }
        if (this.boundPageHide) {
            window.removeEventListener('pagehide', this.boundPageHide);
            this.boundPageHide = null;
        }
        if (this.boundVisibilityChange) {
            document.removeEventListener('visibilitychange', this.boundVisibilityChange);
            this.boundVisibilityChange = null;
        }
        if (this.boundFreeze) {
            document.removeEventListener('freeze', this.boundFreeze);
            this.boundFreeze = null;
        }

        this.flushSync('unload');

        this.isStarted = false;
        this.isStopping = false;
        this.logger.info('Session recording stopped', { instanceId: this.instanceId });

        // Give rrweb time to fully clean up internal mutation observers
        return new Promise(resolve => setTimeout(resolve, RESTART_COOLDOWN_MS));
    }

    updateDistinctId(distinctId: string): void {
        this.distinctId = distinctId;
    }

    private handleEvent(event: eventWithTime): void {
        if (!this.isStarted) return;

        // Ignore events from a stale rrweb instance
        if (SessionRecording.activeInstanceId !== this.instanceId) {
            return;
        }

        // Auto-stop after max duration to prevent runaway data on long-lived tabs
        if (Date.now() - this.startedAt > SessionRecording.MAX_RECORDING_DURATION_MS) {
            this.logger.info('Recording max duration reached, stopping');
            this.stop();
            return;
        }

        this.buffer.push(event);

        // Eager first-batch flush: when the first FullSnapshot (type 2) arrives,
        // schedule a quick flush so the critical first batch gets sent ASAP
        // rather than waiting for the full flush interval.
        if (!this.hasSentFullSnapshot && !this.firstBatchTimer && event.type === 2) {
            this.firstBatchTimer = setTimeout(() => {
                this.firstBatchTimer = null;
                if (!this.hasSentFullSnapshot && this.isStarted) {
                    this.logger.debug('Eager first-batch flush triggered');
                    void this.flush();
                }
            }, 500);
        }

        if (this.buffer.length > this.MAX_BUFFER_SIZE) {
            const before = this.buffer.length;
            this.buffer = this.evictLowPriorityEvents(this.buffer, this.MAX_BUFFER_SIZE);
            const dropped = before - this.buffer.length;
            if (dropped > 0) {
                this.logger.warn('Recording buffer overflow, dropped low-priority events', { dropped });
            }
        }

        if (this.buffer.length >= this.BUFFER_SIZE) {
            // flush() has its own re-entrancy guard — safe to call without await
            void this.flush();
        }
    }

    /**
     * Evict low-priority events to bring buffer under maxSize.
     * Priority (lowest first, dropped first):
     *   1. MouseMove (IncrementalSnapshot type 3, source 1/6)
     *   2. Scroll (IncrementalSnapshot type 3, source 3)
     *   3. Everything else (mutations, full snapshots, inputs, etc.)
     */
    private evictLowPriorityEvents(events: eventWithTime[], maxSize: number): eventWithTime[] {
        if (events.length <= maxSize) return events;

        // Tag each event with priority
        const tagged = events.map((e, i) => {
            let priority = 2; // default: keep

            if (e.type === 3) {
                const source = (e as any).data?.source;
                // source 1 = mousemove, source 6 = touchmove
                if (source === 1 || source === 6) {
                    priority = 0; // lowest — drop first
                }
                // source 3 = scroll
                else if (source === 3) {
                    priority = 1; // drop second
                }
            }

            return { event: e, priority, index: i };
        });

        // Sort by priority ASC (lowest first), then by index ASC (oldest first within same priority)
        tagged.sort((a, b) => a.priority - b.priority || a.index - b.index);

        // Keep the last maxSize entries (highest priority)
        const kept = tagged.slice(-(maxSize));

        // Restore original order
        kept.sort((a, b) => a.index - b.index);

        return kept.map(t => t.event);
    }

    private isRecordingViable(events: eventWithTime[]): boolean {
        if (events.length < MIN_EVENTS_TO_SEND) return false;

        const hasFullSnapshot = events.some(e => e.type === 2);
        if (!hasFullSnapshot) return false;

        const hasIncrementalData = events.some(e => e.type === 3);
        if (!hasIncrementalData) return false;

        const timestamps = events.map(e => e.timestamp).filter(t => t > 0);
        if (timestamps.length >= 2) {
            const minTs = Math.min(...timestamps);
            const maxTs = Math.max(...timestamps);
            if (maxTs - minTs < MIN_RECORDING_DURATION_MS) return false;
        }

        return true;
    }

    private async flush(): Promise<void> {
        if (this.buffer.length === 0) return;
        if (this.isFlushing) return;
        this.isFlushing = true;

        try {
            await this.flushInternal();
        } finally {
            this.isFlushing = false;
        }
    }

    private async flushInternal(): Promise<void> {
        if (this.buffer.length === 0) return;

        // --- FIRST BATCH GATE ---
        // Don't send anything until we have a viable first batch.
        // This prevents ghost/corrupted recordings.
        if (!this.hasSentFullSnapshot) {
            const hasFullSnapshot = this.buffer.some(e => e.type === 2);

            if (!hasFullSnapshot) {
                const bufferAge = Date.now() - this.bufferCreatedAt;

                if (bufferAge < FULL_SNAPSHOT_TIMEOUT_MS && this.buffer.length < this.BUFFER_SIZE) {
                    this.logger.debug('Waiting for full snapshot', {
                        events: this.buffer.length,
                        age: bufferAge,
                    });
                    return;
                }

                // Timeout — we never got a full snapshot. This recording is broken.
                this.logger.warn('Never received full snapshot, discarding buffer', {
                    events: this.buffer.length,
                    age: bufferAge,
                });
                this.buffer = [];
                this.bufferCreatedAt = Date.now();
                return;
            }

            // We have a full snapshot in the buffer. Check age + viability
            // BEFORE committing hasSentFullSnapshot.
            const age = Date.now() - this.startedAt;
            if (age < MIN_RECORDING_DURATION_MS) {
                this.logger.debug('Recording too young, deferring flush', { age });
                return;
            }

            // Check viability before we commit to sending
            const candidateEvents = [...this.buffer];
            if (!this.isRecordingViable(candidateEvents)) {
                this.logger.debug('First batch not yet viable, waiting for more events', {
                    count: candidateEvents.length,
                });
                return;
            }

            // Viable! Take the events and mark as committed.
            this.buffer = [];
            this.bufferCreatedAt = Date.now();

            this.logger.debug('Sending first viable recording batch', { count: candidateEvents.length });
            const sent = await this.sendWithRetry(candidateEvents);
            if (sent) {
                this.hasSentFullSnapshot = true;
            } else {
                // Re-buffer if send failed — but don't set hasSentFullSnapshot
                if (this.buffer.length + candidateEvents.length <= this.MAX_BUFFER_SIZE) {
                    this.buffer = [...candidateEvents, ...this.buffer];
                } else {
                    this.logger.warn('Dropping failed first batch to prevent overflow');
                }
            }
            return;
        }

        // --- SUBSEQUENT BATCHES ---
        // We've already committed to this recording. Send everything.
        if (this.buffer.length < MIN_EVENTS_TO_SEND) {
            // Small buffer — wait for more unless the buffer is getting old
            const bufferAge = Date.now() - this.bufferCreatedAt;
            if (bufferAge < this.FLUSH_INTERVAL_MS * 2) {
                return;
            }
        }

        const events = [...this.buffer];
        this.buffer = [];
        this.bufferCreatedAt = Date.now();

        this.logger.debug('Flushing recording events', { count: events.length });
        await this.sendWithRetry(events);
    }

    /**
     * Send events with retry. Returns true if successful.
     */
    private async sendWithRetry(events: eventWithTime[]): Promise<boolean> {
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                await this.transport.sendRecording(
                    this.distinctId,
                    this.sessionId,
                    this.windowId,
                    events
                );
                return true;
            } catch (error) {
                if (attempt < 2) {
                    const delay = Math.pow(2, attempt) * 500 + Math.random() * 500;
                    this.logger.warn(`Recording send failed, retry in ${delay}ms`, { attempt, error });
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    this.logger.error('Recording send failed after retries', { error });

                    if (this.buffer.length + events.length <= this.MAX_BUFFER_SIZE) {
                        this.buffer = [...events, ...this.buffer];
                    } else {
                        this.logger.warn('Dropping failed events to prevent overflow', {
                            dropped: events.length,
                        });
                    }
                    return false;
                }
            }
        }
        return false;
    }

    /**
     * Synchronous flush via sendBeacon.
     *
     * @param reason - 'unload' for beforeunload/pagehide/freeze/stop (user is leaving — send everything viable)
     *                 'hidden' for visibilitychange hidden (tab may return — be strict about ghost recordings)
     */
    private flushSync(reason: 'unload' | 'hidden'): void {
        if (this.buffer.length === 0) return;

        const events = [...this.buffer];
        this.buffer = [];

        if (!this.hasSentFullSnapshot) {
            const age = Date.now() - this.startedAt;

            if (reason === 'hidden') {
                // Tab hidden: strict — require minimum age to prevent ghost recordings
                // from session rotations followed by an immediate visibility-hidden.
                if (age < MIN_RECORDING_DURATION_MS) {
                    this.logger.debug('Skipping sync flush — recording too young (hidden)', { age, count: events.length });
                    // Put events back — the tab may return
                    this.buffer = events;
                    return;
                }
                if (!this.isRecordingViable(events)) {
                    this.logger.debug('Skipping sync flush — first batch not viable (hidden)', { count: events.length });
                    this.buffer = events;
                    return;
                }
            } else {
                // Unload: user is leaving — be more permissive but still require a full snapshot.
                // Without a full snapshot, the recording is unplayable regardless.
                const hasFullSnapshot = events.some(e => e.type === 2);
                if (!hasFullSnapshot) {
                    this.logger.debug('Skipping sync flush — no full snapshot on unload', { count: events.length });
                    return;
                }
                // Skip if recording is extremely young (< 500ms) — likely a ghost from session rotation
                if (age < 500) {
                    this.logger.debug('Skipping sync flush — recording too young for unload', { age, count: events.length });
                    return;
                }
            }

            this.hasSentFullSnapshot = true;
        }

        this.transport.sendRecordingBeacon(
            this.distinctId,
            this.sessionId,
            this.windowId,
            events
        );
    }
}